import React, { useState } from 'react';
import { Card, Form, Input, Button, message, Typography, Space, Divider, Tabs, Alert } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, PhoneOutlined, ShopOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import axios from 'axios';
import OtpInput from './OtpInput';
import ReCAPTCHA from 'react-google-recaptcha';

const { Title, Text, Link } = Typography;
const { TabPane } = Tabs;

const MerchantLogin = ({ onLogin }) => {
  const [loginForm] = Form.useForm();
  const [registerForm] = Form.useForm();
  const [loginLoading, setLoginLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [showOtp, setShowOtp] = useState(false);
  const [otpType, setOtpType] = useState('login');
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingUserData, setPendingUserData] = useState(null);
  const [loginStep, setLoginStep] = useState('credentials'); // 'credentials' or 'otp'
  // Token reCAPTCHA sekali pakai: WAJIB di-reset di blok catch, kalau tidak
  // percobaan berikutnya selalu gagal dan terlihat seperti sandi salah.
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = React.useRef(null);

  const handleLogin = async (values) => {
    try {
      setLoginLoading(true);
      console.log('🔐 Attempting login with email:', values.email);

      const response = await axios.post('/api/merchant/auth/login', { ...values, captcha_token: captchaToken });
      console.log('📨 Login response:', response.data);
      console.log('📧 Requires OTP:', response.data.requires_otp);

      if (response.data.requires_otp) {
        // Login requires OTP verification
        console.log('✅ Login credentials verified, redirecting to OTP');
        setPendingEmail(values.email);
        setOtpType('login');
        setShowOtp(true);
        setLoginStep('otp');
        message.success('Kredensial berhasil diverifikasi. Silakan periksa email Anda untuk kode OTP.');

        // Store user data temporarily
        localStorage.setItem('temp_user_data', JSON.stringify(response.data.member));
        alert('🔑 OTP telah dikirim ke: ' + values.email + '. Silakan periksa email Anda!');
      } else {
        // Development mode: OTP skipped, login completed directly
        console.log('✅ Development mode - OTP skipped, completing login');
        message.success('Login berhasil! (Development Mode - OTP skipped)');
        handleLoginSuccess(response.data);
      }

    } catch (error) {
      console.error('❌ Login error:', error);
      console.error('❌ Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || 'Login gagal. Silakan coba lagi.';
      message.error(errorMessage);
      // Token reCAPTCHA hangus sekali dipakai — reset supaya mitra bisa mencoba lagi.
      try { captchaRef.current?.reset(); } catch (e) { /* abaikan */ }
      setCaptchaToken(null);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (values) => {
    try {
      setRegisterLoading(true);

      // Remove confirm password from values
      const { confirm_password, ...registerData } = values;

      const response = await axios.post('/api/merchant/auth/register', registerData);

      if (response.data.requires_email_verification) {
        // Registration requires email verification
        setPendingEmail(values.email);
        setOtpType('registration');
        setShowOtp(true);
        setPendingUserData(response.data);
        message.success('Pendaftaran berhasil! Silakan periksa email Anda untuk kode verifikasi.');
      } else {
        // Direct registration (fallback)
        message.success('Registrasi berhasil! Silakan login dengan akun Anda.');
        setActiveTab('login');
        registerForm.resetFields();
      }

    } catch (error) {
      let errorMessage = 'Registrasi gagal. Silakan coba lagi.';

      if (error.response?.data) {
        const responseData = error.response.data;
        if (responseData.error === 'Email or phone already registered') {
          errorMessage = 'Email atau nomor telepon sudah terdaftar. Gunakan data lain.';
        } else if (responseData.error === 'Missing required fields') {
          errorMessage = 'Mohon lengkapi semua field yang diperlukan.';
        } else if (responseData.error) {
          errorMessage = responseData.error;
        }
        if (responseData.message && responseData.message !== responseData.error) {
          errorMessage += ' (' + responseData.message + ')';
        }
      } else if (error.message) {
        errorMessage = 'Koneksi gagal. Periksa internet Anda.';
      }

      message.error(errorMessage);
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleOtpSuccess = (otpData) => {
    if (otpType === 'registration') {
      // Email verification successful
      message.success('Email berhasil diverifikasi! Akun Anda sekarang aktif.');
      setShowOtp(false);
      setActiveTab('login');
      registerForm.resetFields();
      setPendingUserData(null);
    } else if (otpType === 'login') {
      // OTP verification successful for login
      const tempUserData = localStorage.getItem('temp_user_data');
      if (tempUserData) {
        const userData = JSON.parse(tempUserData);
        localStorage.removeItem('temp_user_data');

        // Complete login with tokens from OTP verification
        handleLoginSuccess({
          ...otpData,
          member: userData
        });
      }
    }
    setPendingEmail('');
  };

  const handleLoginSuccess = (loginData) => {
    // Clear any existing admin tokens only if we're in a merchant route
    if (window.location.pathname.startsWith('/merchant')) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }

    // Store merchant tokens and user data
    localStorage.setItem('merchant_access_token', loginData.access_token);
    localStorage.setItem('merchant_refresh_token', loginData.refresh_token);
    localStorage.setItem('merchant_user', JSON.stringify(loginData.member));

    message.success('Login berhasil!');
    onLogin(loginData);
  };

  const handleOtpCancel = () => {
    setShowOtp(false);
    setPendingEmail('');
    setPendingUserData(null);
    setLoginStep('credentials');
    localStorage.removeItem('temp_user_data');
  };

  const handleOtpBack = () => {
    setShowOtp(false);
    if (otpType === 'registration') {
      setActiveTab('register');
    } else {
      setLoginStep('credentials');
    }
  };

  const handleResendOtp = async (type = 'login') => {
    try {
      const response = await axios.post('/api/merchant/auth/resend-otp', {
        email: pendingEmail,
        type
      });

      message.success('OTP baru telah dikirim ke email Anda');
    } catch (error) {
      console.error('Resend OTP error:', error);
      const errorMessage = error.response?.data?.message || 'Gagal mengirim OTP baru. Silakan coba lagi.';
      message.error(errorMessage);
    }
  };

  // Show OTP verification screen
  if (showOtp && pendingEmail) {
    return (
      <OtpInput
        email={pendingEmail}
        type={otpType}
        onSuccess={handleOtpSuccess}
        onCancel={handleOtpCancel}
        onBack={handleOtpBack}
        expiresIn={300} // 5 minutes
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }}>
      <div className="w-full max-w-md">
        <Card
          className="shadow-2xl border-0"
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px'
          }}
        >
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
              <ShopOutlined style={{ fontSize: '32px', color: 'white' }} />
            </div>
            <Title level={2} style={{ margin: 0, color: '#1e40af' }}>
              Merchant Portal
            </Title>
            <Text type="secondary">
              Kelola bisnis Anda dengan mudah
            </Text>
          </div>

          <Tabs activeKey={activeTab} onChange={setActiveTab} centered>
            <TabPane tab="Masuk" key="login">
              <Form
                form={loginForm}
                name="merchant-login"
                onFinish={handleLogin}
                layout="vertical"
                size="large"
              >
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: 'Email diperlukan!' },
                    { type: 'email', message: 'Format email tidak valid!' }
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="Email"
                    style={{ borderRadius: '10px' }}
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[{ required: true, message: 'Password diperlukan!' }]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Password"
                    style={{ borderRadius: '10px' }}
                  />
                </Form.Item>

                <Form.Item>
                  <ReCAPTCHA
                    ref={captchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={(t) => setCaptchaToken(t)}
                    onExpired={() => setCaptchaToken(null)}
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loginLoading}
                    disabled={!captchaToken}
                    block
                    style={{
                      height: '48px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                      border: 'none',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}
                  >
                    {loginLoading ? 'Sedang Masuk...' : 'Masuk'}
                  </Button>
                </Form.Item>

                <div className="text-center">
                  <Button
                    type="link"
                    size="small"
                    onClick={() => handleResendOtp('login')}
                    disabled={!pendingEmail}
                  >
                    Kirim Ulang OTP
                  </Button>
                </div>
              </Form>
            </TabPane>

            <TabPane tab="Daftar" key="register">
              <Form
                form={registerForm}
                name="merchant-register"
                onFinish={handleRegister}
                layout="vertical"
                size="large"
              >
                <Form.Item
                  name="full_name"
                  rules={[{ required: true, message: 'Nama lengkap diperlukan!' }]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Nama Lengkap"
                    style={{ borderRadius: '10px' }}
                  />
                </Form.Item>

                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: 'Email diperlukan!' },
                    { type: 'email', message: 'Format email tidak valid!' }
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="Email"
                    style={{ borderRadius: '10px' }}
                  />
                </Form.Item>

                <Form.Item
                  name="phone"
                  rules={[{ required: true, message: 'Nomor telepon diperlukan!' }]}
                >
                  <Input
                    prefix={<PhoneOutlined />}
                    placeholder="Nomor Telepon"
                    style={{ borderRadius: '10px' }}
                  />
                </Form.Item>

                <Form.Item
                  name="company_name"
                  rules={[{ required: true, message: 'Nama perusahaan diperlukan!' }]}
                >
                  <Input
                    prefix={<ShopOutlined />}
                    placeholder="Nama Perusahaan"
                    style={{ borderRadius: '10px' }}
                  />
                </Form.Item>

                <Form.Item
                  name="business_type"
                  rules={[{ required: true, message: 'Jenis bisnis diperlukan!' }]}
                >
                  <Input
                    prefix={<SafetyCertificateOutlined />}
                    placeholder="Jenis Bisnis (Contoh: Retail, Technology, etc)"
                    style={{ borderRadius: '10px' }}
                  />
                </Form.Item>

                <Form.Item
                  name="address"
                  rules={[{ required: true, message: 'Alamat diperlukan!' }]}
                >
                  <Input
                    prefix={<ShopOutlined />}
                    placeholder="Alamat Lengkap"
                    style={{ borderRadius: '10px' }}
                  />
                </Form.Item>

                <Form.Item
                  name="city"
                  rules={[{ required: true, message: 'Kota diperlukan!' }]}
                >
                  <Input
                    prefix={<ShopOutlined />}
                    placeholder="Kota"
                    style={{ borderRadius: '10px' }}
                  />
                </Form.Item>

                <Form.Item
                  name="state"
                  rules={[{ required: true, message: 'Provinsi diperlukan!' }]}
                >
                  <Input
                    prefix={<ShopOutlined />}
                    placeholder="Provinsi"
                    style={{ borderRadius: '10px' }}
                  />
                </Form.Item>

                <Form.Item
                  name="postal_code"
                  rules={[{ required: true, message: 'Kode pos diperlukan!' }]}
                >
                  <Input
                    prefix={<ShopOutlined />}
                    placeholder="Kode Pos"
                    style={{ borderRadius: '10px' }}
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: 'Password diperlukan!' },
                    { min: 6, message: 'Password minimal 6 karakter!' }
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Password"
                    style={{ borderRadius: '10px' }}
                  />
                </Form.Item>

                <Form.Item
                  name="confirm_password"
                  dependencies={['password']}
                  rules={[
                    { required: true, message: 'Konfirmasi password diperlukan!' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Password tidak cocok!'));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Konfirmasi Password"
                    style={{ borderRadius: '10px' }}
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={registerLoading}
                    block
                    style={{
                      height: '48px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                      border: 'none',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}
                  >
                    {registerLoading ? 'Sedang Mendaftar...' : 'Daftar Sekarang'}
                  </Button>
                </Form.Item>
              </Form>
            </TabPane>
          </Tabs>

          <Divider />

          <div className="text-center">
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Dengan masuk, Anda setuju dengan{' '}
              <Link>Syarat & Ketentuan</Link> dan <Link>Kebijakan Privasi</Link> kami
            </Text>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MerchantLogin;